import { trackUserAction } from "@dashai/utils/trackUserAction";
import { track } from "@/analytics";

const SEARCH_DEBOUNCE_MS = 500;
const searchTimers = {};

const currentPage = () => {
  if (typeof window === "undefined") return "";
  return window.location?.pathname || "";
};

const isEmptyFilter = (payload) => {
  if (payload == null) return true;
  if (Array.isArray(payload)) return payload.length === 0;
  if (typeof payload === "object") return Object.keys(payload).length === 0;
  return false;
};

const flushTrack = (event) => {
  try {
    trackUserAction(event);
  } catch {
    /* never let tracking break the store */
  }
};

// Dual-write the same filter/search signals to the governed behavior_events
// catalog alongside the legacy trackUserAction emits above. The legacy
// writes stay so the shipped admin read layer is untouched; these add the
// consolidation-target events. Two catalog invariants enforced here:
//   - filter_applied.filter_value is a single categorical string only — omitted
//     for array/object/compound selections (multi-select, regions+markets).
//   - search_performed carries term_length only, never the raw term.
// The catalog has no "cleared" event, so a filter clear emits nothing here.
const catalogFilterApplied = (filterKey, rawValue) => {
  const payload = { filter_key: filterKey };
  if (typeof rawValue === "string" && rawValue) payload.filter_value = rawValue;
  track("filter_applied", payload);
};
const catalogSearchPerformed = (scope, term) => {
  const safe = typeof term === "string" ? term : "";
  track("search_performed", { search_scope: scope, term_length: safe.length });
};

// Reads the currently-selected workspace ID from auth state. Mirrors how
// GeneralLayout.jsx tags dashboard events with selectedWorkspace?.key so
// filter / search events carry the same id_client and Live Events can show
// which workspace the user was in when they applied the filter.
const currentWorkspaceKey = (store) => {
  try {
    return store.getState()?.auth?.selectedWorkspace?.key || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Redux middleware that emits user-action events on a small allowlist of
 * filter and search actions. Search events are debounced per-dimension so
 * we don't fire one event per keystroke. Every event is tagged with the
 * caller's current workspace (`id_client`) so the admin Live Events panel
 * can show the workspace context the action ran in.
 *
 * Search payload note: search_typed carries the literal `search_term`
 * (and a redundant `term_length` for clients pinned to the older shape).
 * The Live Events panel that consumes these events is gated to Dashmote
 * employees with the user-level `dashmote_admin` flag (see useAdminStatus.ts), so
 * the search content stays inside the internal team. Revisit if the
 * audience for that panel ever widens beyond Dashmote internal.
 */
export const trackingMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  const page = currentPage();
  const idClient = currentWorkspaceKey(store);

  switch (action.type) {
    case "filters/setColumnFilter": {
      const empty = isEmptyFilter(action.payload);
      flushTrack({
        eventType: empty ? "filter_cleared" : "filter_applied",
        idClient,
        metadata: { filter_key: "column_filters", page },
      });
      if (!empty) catalogFilterApplied("column_filters");
      break;
    }
    case "filters/removeFilterByKey":
      flushTrack({
        eventType: "filter_cleared",
        idClient,
        metadata: { filter_key: action.payload, page },
      });
      break;
    case "filters/clearAllFilters":
      flushTrack({
        eventType: "filter_cleared",
        idClient,
        metadata: { filter_key: "__all__", page },
      });
      break;
    case "personaConfig/toggleExtra": {
      // "Add to home" usage complements show_all_toggled: repeated adds of
      // the same items across users are the signal that a persona's set is
      // missing them.
      const { kind, id, on } = action.payload || {};
      const state = store.getState();
      const uid = state.auth?.user?.uid;
      // Configs are stored per workspace, so the persona lives under
      // byWorkspace[idClient]. The flat fallback covers a rehydrated blob
      // written before that nesting, which the slice migrates on next write.
      const entry = uid ? state.personaConfig?.configsByUid?.[uid] : null;
      const persona =
        entry?.byWorkspace?.[idClient]?.persona || entry?.persona || "none";
      flushTrack({
        eventType: "home_extra_toggled",
        idClient,
        metadata: { kind, id, on, persona, page },
      });
      break;
    }
    case "personaConfig/showAllToggled": {
      // Show-all usage is the success metric for the persona-based
      // sidebar/home: a persona whose users constantly reveal the hidden set
      // has the wrong set.
      const { list, on, persona, surface } = action.payload || {};
      flushTrack({
        eventType: "show_all_toggled",
        idClient,
        metadata: { list, on, persona: persona || "none", surface, page },
      });
      break;
    }
    case "filters/setCurrentCountry":
      if (action.payload) {
        flushTrack({
          eventType: "filter_applied",
          idClient,
          metadata: {
            filter_key: "country",
            filter_value: action.payload,
            page,
          },
        });
        catalogFilterApplied("country", action.payload);
      }
      break;
    case "filterDimensions/search/pending":
    case "physicalFilterDimensions/search/pending": {
      // Both filter-dimension slices follow the same async-thunk shape; track them
      // through the same path so search behaviour on physical-location /
      // physical-franchise dropdowns is captured alongside the execution-view ones.
      const dim = action.meta?.arg?.dimension;
      const term = action.meta?.arg?.search ?? "";
      if (!dim) break;
      const timerKey = `${action.type}|${dim}`;
      if (searchTimers[timerKey]) clearTimeout(searchTimers[timerKey]);
      searchTimers[timerKey] = setTimeout(() => {
        const safeTerm = typeof term === "string" ? term : "";
        flushTrack({
          eventType: "search_typed",
          idClient: currentWorkspaceKey(store),
          metadata: {
            search_scope: dim,
            search_term: safeTerm,
            term_length: safeTerm.length,
            page,
          },
        });
        catalogSearchPerformed(dim, safeTerm);
      }, SEARCH_DEBOUNCE_MS);
      break;
    }
    // Market Overview's per-dashboard selectors all live on a separate slice
    // (state.marketOverview) and don't go through filtersSlice, so the
    // generic filters/* handlers above miss them. Emit one filter_applied per
    // dimension when the user changes a selector; emit filter_cleared when
    // the payload empties. Carries filter_value so the admin Live Events
    // panel can show "Platform: UberEats" rather than just "Platform".
    case "marketOverview/setSelectedRegion":
    case "marketOverview/setSelectedPlatform":
    case "marketOverview/setSelectedSegment":
    case "marketOverview/setSelectedFranchise":
    case "marketOverview/setSelectedIsChain":
    case "marketOverview/setSelectedMetric": {
      const filterKey = action.type.replace("marketOverview/setSelected", "");
      const empty = isEmptyFilter(action.payload);
      flushTrack({
        eventType: empty ? "filter_cleared" : "filter_applied",
        idClient,
        metadata: {
          filter_key: filterKey,
          filter_value: action.payload,
          page,
        },
      });
      // filter_value populated only for single-string selectors (Platform,
      // Segment, IsChain, Metric); array selectors (Franchise) omit it.
      if (!empty) catalogFilterApplied(filterKey, action.payload);
      break;
    }
    case "marketOverview/setSelectedRegionsAndMarkets": {
      // Compound payload (regions/markets/regionTotals/parentToChild). Track only
      // the user-visible summary — region titles + market titles — to keep the
      // event readable.
      const payload = (action.payload && typeof action.payload === "object") ? action.payload : {};
      const regionTitles = Array.isArray(payload.regions) ? payload.regions.map((r) => r?.title || r?.key).filter(Boolean) : [];
      const marketTitles = Array.isArray(payload.markets) ? payload.markets.map((m) => m?.market || m?.key).filter(Boolean) : [];
      const empty = regionTitles.length === 0 && marketTitles.length === 0;
      flushTrack({
        eventType: empty ? "filter_cleared" : "filter_applied",
        idClient,
        metadata: {
          filter_key: "RegionsAndMarkets",
          filter_value: { regions: regionTitles, markets: marketTitles },
          page,
        },
      });
      // Compound selection → no categorical filter_value.
      if (!empty) catalogFilterApplied("RegionsAndMarkets");
      break;
    }
    case "marketOverview/setPlatformSearchText": {
      // Treat the platform-name search box like filterDimensions/search:
      // debounce + emit search_typed so we don't fire one event per keystroke.
      const term = action.payload ?? "";
      const dim = "market_overview_platform";
      const timerKey = `${action.type}|${dim}`;
      if (searchTimers[timerKey]) clearTimeout(searchTimers[timerKey]);
      searchTimers[timerKey] = setTimeout(() => {
        const safeTerm = typeof term === "string" ? term : "";
        flushTrack({
          eventType: "search_typed",
          idClient: currentWorkspaceKey(store),
          metadata: {
            search_scope: dim,
            search_term: safeTerm,
            term_length: safeTerm.length,
            page,
          },
        });
        catalogSearchPerformed(dim, safeTerm);
      }, SEARCH_DEBOUNCE_MS);
      break;
    }
    case "competitors/setSelectedCompetitors": {
      const empty = isEmptyFilter(action.payload);
      flushTrack({
        eventType: empty ? "filter_cleared" : "filter_applied",
        idClient,
        metadata: { filter_key: "competitors", filter_value: action.payload, page },
      });
      // Array selection → no categorical filter_value.
      if (!empty) catalogFilterApplied("competitors", action.payload);
      break;
    }
    default:
      break;
  }

  return result;
};
